/**
 * Widget overlay — injects translated elements on top of the Chatbase
 * iframe so that widget UI text (which is inside a cross-origin iframe
 * and otherwise unmodifiable) appears in the active language.
 *
 * Covers: header title, 3-dot menu, and footer disclaimer.
 * All overlays live inside `#chatbase-bubble-window`.
 */

import type { TranslationMap } from "../types";
import { t } from "../i18n";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OVERLAY_ID = "__singular_chatbase_menu_overlay";
const TITLE_ID = "__singular_chatbase_title_overlay";
const DISCLAIMER_ID = "__singular_chatbase_disclaimer_overlay";
const MENU_ID = "__singular_chatbase_menu_dropdown";
const STYLES_ID = "__singular_chatbase_overlay_styles";

/** Translation keys for the three menu items. */
const MENU_KEYS = {
  newChat: "widget.menu.newChat",
  endChat: "widget.menu.endChat",
  recentChats: "widget.menu.recentChats",
} as const;

const TITLE_KEY = "widget.title";
const DISCLAIMER_KEY = "widget.disclaimer";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getBubbleWindow(): HTMLElement | null {
  /* v8 ignore next — SSR guard unreachable in jsdom */
  if (typeof document === "undefined") return null;
  return document.getElementById("chatbase-bubble-window");
}

function getChatbaseIframe(): HTMLIFrameElement | null {
  /* v8 ignore next — SSR guard unreachable in jsdom */
  if (typeof document === "undefined") return null;
  const bubbleWindow = getBubbleWindow();
  if (!bubbleWindow) return null;
  return bubbleWindow.querySelector<HTMLIFrameElement>("iframe");
}

function injectStyles(): void {
  /* v8 ignore next — defensive dedup; removeOverlay always removes styles before re-inject */
  if (document.getElementById(STYLES_ID)) return;

  const style = document.createElement("style");
  style.id = STYLES_ID;
  style.textContent = `
    #${OVERLAY_ID} {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      height: 64px;
      z-index: 1;
      pointer-events: none;
    }

    #${OVERLAY_ID} .scb-menu-btn {
      pointer-events: auto;
      position: absolute;
      top: 10px;
      right: 48px;
      width: 44px;
      height: 44px;
      border: none;
      background: var(--surface-raised, #fff);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(0, 0, 0, 0.64);
      border-radius: 6px;
      padding: 0;
    }
    #${OVERLAY_ID} .scb-menu-btn:hover { color: rgba(0, 0, 0, 1); }

    #${MENU_ID} {
      position: absolute;
      top: 54px;
      right: 42px;
      min-width: 170px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05);
      padding: 4px;
      z-index: 2;
      pointer-events: auto;
      display: none;
      flex-direction: column;
    }

    #${MENU_ID}.scb-open { display: flex; }

    #${MENU_ID} .scb-menu-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border: none;
      background: transparent;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      color: #1a1a1a;
      border-radius: 4px;
      text-decoration: none;
      line-height: 1.4;
      white-space: nowrap;
      font-family: inherit;
    }
    #${MENU_ID} .scb-menu-item:hover { background: #f5f5f5; }

    #${TITLE_ID} {
      position: absolute;
      top: 0;
      left: 0;
      right: 100px;
      height: 64px;
      z-index: 1;
      display: flex;
      align-items: center;
      padding-left: 56px;
      pointer-events: none;
      background: var(--surface-raised, #fff);
    }

    #${TITLE_ID} .scb-title-text {
      font-weight: 600;
      font-size: 14px;
      letter-spacing: -0.01em;
      color: #1a1a1a;
      line-height: 20px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    #${DISCLAIMER_ID} {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 70px !important;
      z-index: 1;
      background: var(--surface-raised, #fff);
      padding: 8px 16px;
      pointer-events: auto;
    }

    #${DISCLAIMER_ID} .scb-disclaimer-text {
      width: 100%;
      overflow-x: hidden;
      white-space: pre-wrap;
      word-break: break-word;
      text-align: center;
      font-weight: 500;
      font-size: 10px;
      letter-spacing: -0.01em;
      color: #666;
      line-height: 1.4;
    }

    #${DISCLAIMER_ID} .scb-disclaimer-text p {
      margin: 0;
    }

    #${DISCLAIMER_ID} .scb-disclaimer-text a {
      color: var(--primary, #5c6ae1);
      text-decoration: underline;
      cursor: pointer;
    }
  `;
  document.head.appendChild(style);
}

// SVG icons matching Chatbase's style
const ICON_DOTS = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="5" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></svg>`;

const ICON_NEW_CHAT = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/></svg>`;

const ICON_END_CHAT = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;

const ICON_RECENT = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let _language = "en";
let _translations: TranslationMap = {};
let _botId = "";
let _domain = "www.chatbase.co";
let _onResetChat: (() => void) | null = null;
let _clickOutsideListener: ((e: MouseEvent) => void) | null = null;

// ---------------------------------------------------------------------------
// Public API (internal to library)
// ---------------------------------------------------------------------------

export interface OverlayConfig {
  language: string;
  translations: TranslationMap;
  botId: string;
  domain?: string;
  onResetChat: () => void;
}

/**
 * Install the translated menu overlay into the Chatbase widget window.
 *
 * Must be called after the widget is initialised and the
 * `#chatbase-bubble-window` element exists in the DOM.
 */
export function installOverlay(config: OverlayConfig): void {
  if (typeof document === "undefined") return;

  _language = config.language;
  _translations = config.translations;
  _botId = config.botId;
  _domain = config.domain ?? "www.chatbase.co";
  _onResetChat = config.onResetChat;

  // Don't show overlay if language matches the default (iframe already shows English)
  if (_language === "en") {
    removeOverlay();
    return;
  }

  removeOverlay();
  injectStyles();

  const bubbleWindow = getBubbleWindow();
  if (!bubbleWindow) return;

  // --- Header title overlay ---
  const titleOverlay = document.createElement("div");
  titleOverlay.id = TITLE_ID;
  const titleText = document.createElement("span");
  titleText.className = "scb-title-text";
  titleText.textContent = t(TITLE_KEY, _language, _translations);
  titleOverlay.appendChild(titleText);
  bubbleWindow.appendChild(titleOverlay);

  // --- Menu button overlay ---
  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;

  // 3-dot menu button
  const menuBtn = document.createElement("button");
  menuBtn.className = "scb-menu-btn";
  menuBtn.innerHTML = ICON_DOTS;
  menuBtn.addEventListener("click", handleMenuToggle);
  overlay.appendChild(menuBtn);

  // Dropdown menu
  const dropdown = document.createElement("div");
  dropdown.id = MENU_ID;

  dropdown.appendChild(createMenuItem(ICON_NEW_CHAT, MENU_KEYS.newChat, handleNewChat));
  dropdown.appendChild(createMenuItem(ICON_END_CHAT, MENU_KEYS.endChat, handleEndChat));
  dropdown.appendChild(createMenuItem(ICON_RECENT, MENU_KEYS.recentChats, handleRecentChats));

  overlay.appendChild(dropdown);
  bubbleWindow.appendChild(overlay);

  // --- Footer disclaimer overlay ---
  const disclaimerOverlay = document.createElement("div");
  disclaimerOverlay.id = DISCLAIMER_ID;
  const disclaimerText = document.createElement("div");
  disclaimerText.className = "scb-disclaimer-text";
  disclaimerText.innerHTML = t(DISCLAIMER_KEY, _language, _translations);
  disclaimerOverlay.appendChild(disclaimerText);
  bubbleWindow.appendChild(disclaimerOverlay);

  // Click-outside listener to close menu
  _clickOutsideListener = (e: MouseEvent) => {
    const dd = document.getElementById(MENU_ID);
    if (!dd?.classList.contains("scb-open")) return;
    if (!overlay.contains(e.target as Node)) {
      dd.classList.remove("scb-open");
    }
  };
  document.addEventListener("click", _clickOutsideListener, true);
}

/** Remove all overlays from the DOM. */
export function removeOverlay(): void {
  if (typeof document === "undefined") return;

  document.getElementById(OVERLAY_ID)?.remove();
  document.getElementById(MENU_ID)?.remove();
  document.getElementById(TITLE_ID)?.remove();
  document.getElementById(DISCLAIMER_ID)?.remove();
  document.getElementById(STYLES_ID)?.remove();

  if (_clickOutsideListener) {
    document.removeEventListener("click", _clickOutsideListener, true);
    _clickOutsideListener = null;
  }
}

/** Update all overlay text for a new language. */
export function updateOverlayLanguage(language: string, translations: TranslationMap): void {
  _language = language;
  _translations = translations;

  if (language === "en") {
    removeOverlay();
    return;
  }

  // Update title
  const titleEl = document.getElementById(TITLE_ID)?.querySelector(".scb-title-text");
  if (titleEl) {
    titleEl.textContent = t(TITLE_KEY, language, translations);
  }

  // Update disclaimer
  const disclaimerEl = document
    .getElementById(DISCLAIMER_ID)
    ?.querySelector(".scb-disclaimer-text");
  if (disclaimerEl) {
    disclaimerEl.innerHTML = t(DISCLAIMER_KEY, language, translations);
  }

  // Update menu items
  const dropdown = document.getElementById(MENU_ID);
  if (!dropdown) return;

  const items = dropdown.querySelectorAll<HTMLElement>(".scb-menu-item");
  const keys = [MENU_KEYS.newChat, MENU_KEYS.endChat, MENU_KEYS.recentChats];
  items.forEach((item, i) => {
    const span = item.querySelector("span");
    if (span && keys[i]) {
      span.textContent = t(keys[i], language, translations);
    }
  });
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function createMenuItem(
  iconSvg: string,
  translationKey: string,
  handler: () => void,
): HTMLButtonElement {
  const item = document.createElement("button");
  item.className = "scb-menu-item";
  item.innerHTML = `${iconSvg}<span>${t(translationKey, _language, _translations)}</span>`;
  item.addEventListener("click", () => {
    closeDropdown();
    handler();
  });
  return item;
}

function handleMenuToggle(e: Event): void {
  e.stopPropagation();
  const dropdown = document.getElementById(MENU_ID);
  /* v8 ignore next — dropdown always exists when this listener is attached */
  if (!dropdown) return;
  dropdown.classList.toggle("scb-open");
}

function closeDropdown(): void {
  document.getElementById(MENU_ID)?.classList.remove("scb-open");
}

function handleNewChat(): void {
  _onResetChat?.();
}

function handleEndChat(): void {
  const iframe = getChatbaseIframe();
  if (!iframe) return;
  const base = `https://${_domain}/chatbot-iframe/${_botId}`;
  const params = new URLSearchParams(new URL(iframe.src).search);
  iframe.src = `${base}/end-chat?${params.toString()}`;
}

function handleRecentChats(): void {
  const iframe = getChatbaseIframe();
  if (!iframe) return;
  const base = `https://${_domain}/chatbot-iframe/${_botId}`;
  const params = new URLSearchParams(new URL(iframe.src).search);
  iframe.src = `${base}/recent-chats?${params.toString()}`;
}
